import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Checkbox, Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {SELECT_USER, SET_USER_LIST, UserContext} from './context/context'
import UserTable from './UserTable'
import UserFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const Users = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(UserContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [showDeleted, setShowDeleted] = useState(false)
  const currentUrl = useRef(null)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const isActive = !showDeleted

  const getUserListData = async (url = null) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'users.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url
      ? url
      : `/api/users/?search=${search}&is_active=${isActive}`
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_USER_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'users.loadError' }))
    }
  }

  useEffect(() => {
    getUserListData()
  }, [])

  useEffect(() => {
    const url = `/api/users/?search=${search}&is_active=${isActive}`
    currentUrl.current = url
    getUserListData(url)
  }, [debouncedSearch, showDeleted])

  const deleteUser = async (record) => {
    let res = await deleteRequest(
      `/api/users/delete/${record.email}/${isActive}/`
    )
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: showDeleted ? 'users.restoreSuccess' : 'users.deleteSuccess',
          }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getUserListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'users.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'users.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'users.confirmDeleteContent' },
        {
          action: intl
            .formatMessage({
              id: showDeleted ? 'users.restore' : 'users.delete',
            })
            .toLowerCase(),
          email: record.email,
        }
      ),
      onOk: () => deleteUser(record),
      centered: true,
      maskClosable: true,
    })
  }

  const onCreateUser = () => {
    dispatch({
      type: SELECT_USER,
      data: { selected_user: {}, show_user_popup: true },
    })
  }

  const onEditUser = (record) => {
    dispatch({
      type: SELECT_USER,
      data: { selected_user: record, show_user_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.user_list?.length) return []
    return state.user_list.map((item, index) => ({
      ...item,
      key: `user-${index}`,
      rowKey: `user-${item.email}`,
      index,
    }))
  }, [state.user_list])

  return (
    <>
      {contextHolder}

      {state.show_user_popup && (
        <UserFormModal getUserListData={getUserListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex
              justify="space-between"
              gap={16}
              align="center"
              className="full-width"
            >
              <InputText
                name="search_users"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'users.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-users"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Space
                  direction="horizontal"
                  size="middle"
                  className="ant-checkbox-group ant-form-item-inline"
                >
                  <Checkbox
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  >
                    {intl.formatMessage({
                      id: showDeleted ? 'users.hideDeleted' : 'users.showDeleted',
                    })}
                  </Checkbox>
                </Space>
                <Button onClick={onCreateUser}>
                  {intl.formatMessage({ id: 'users.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <UserTable
              dataSource={dataSource}
              pagination={pagination}
              getUserListData={getUserListData}
              onEdit={onEditUser}
              onDelete={confirmDelete}
              showDeleted={showDeleted}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default Users
